import { useEffect, useMemo } from 'react';

import useAuth from 'hooks/auth/use-auth';
import { useOrderDetails } from 'hooks/use-order-details';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import { IUseRefundEligibilityOptions, IUseRefundEligibiltyResponse } from './types';
import { useCheckOrderRefundEligibility } from './use-check-order-refund-eligibility';
import { useCheckUserRefundEligibility } from './use-check-user-refund-eligibility';
import { getFirstMessage } from './utils';

/*
 * Determines whether or not a user and the order permit performing
 * a customer initiated refund.
 *
 * Includes details about the refund eligibility along with
 * reason text for messaging.
 */
export const useRefundEligibility = ({
  preloadedOrder,
  orderId
}: IUseRefundEligibilityOptions = {}): IUseRefundEligibiltyResponse => {
  const { order: retrievedOrder, loading: isOrderLoading } = useOrderDetails(orderId);

  const order = preloadedOrder || retrievedOrder;

  const orderEligibilityData = useCheckOrderRefundEligibility(order);
  const userEligibilityData = useCheckUserRefundEligibility();
  const { loading: isUserRefundEligibilityLoading } = userEligibilityData;

  const mParticle = useMParticleContext();
  const { user } = useAuth();

  const refundIneligibilityReason = getFirstMessage([
    userEligibilityData.refundIneligibilityReason,
    orderEligibilityData.refundIneligibilityReason
  ]);

  const isDeterminingRefundEligibility =
    isUserRefundEligibilityLoading || (!preloadedOrder && isOrderLoading);

  const result: IUseRefundEligibiltyResponse = useMemo(
    () => ({
      ...orderEligibilityData,
      ...userEligibilityData,
      refundIneligibilityReason,
      isDeterminingRefundEligibility
    }),
    [
      isDeterminingRefundEligibility,
      orderEligibilityData,
      refundIneligibilityReason,
      userEligibilityData
    ]
  );

  useEffect(() => {
    if (!isDeterminingRefundEligibility) {
      mParticle.logEvent(CustomEventNames.REFUND_ELIGIBILITY_CHECK, EventTypes.Other, {
        message: `Refund eligibility check results`,
        orderId: order?.rbiOrderId,
        ...result
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeterminingRefundEligibility, mParticle, order?.rbiOrderId, result, user?.cognitoId]);

  return result;
};
